import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { linkCausedBy, unlinkCausedBy } from '../api/changeManagement'
import { ticketQuery, ticketSubordinatesQuery } from '../api/easeedesk'
import { setTicketParent } from '../api/easeedeskTicketTabs'
import { toast } from '../ui/notify'
import { SelectPicker } from '../ui/SelectPicker'
import { Spinner } from '../ui/Spinner'
import './TicketSubordinates.css'

/**
 * Fila "Subordinates" - `easeedesk/ticket/actions/subordinates.php`.
 *
 * Lista de alegere, nu tabel: un tabel cerea derulat printr-o lista de zeci de
 * randuri ca sa se apese pe unul. Si e lista lui bootstrap-select
 * (`selectpicker` cu `data-live-search`), nu <select>-ul gol al browserului:
 * macheta il porneste chiar ea in scriptul de jos, iar cine stie numarul sau un
 * cuvant din subiect ajunge la tichet din trei taste.
 *
 * "Set as Parent" scrie prin `POST tickets/{id}/parent` (`easeedesk/setTicketParent`),
 * si se poarta ca scriptul machetei: fara alegere spune "Choose a ticket
 * first."; pe refuz arata motivul serverului si lasa butonul apasabil; pe
 * reusita reincarca fila - tichetul tocmai legat n-are ce cauta in lista de
 * candidati, iar sus trebuie sa apara printre subordonate - si banda de
 * rudenie a fisei, care il arata si ea.
 */

export function TicketSubordinates({ id }: { id: number }) {
  const { data, error } = useQuery(ticketSubordinatesQuery(id))
  const [child, setChild] = useState('')
  const [saving, setSaving] = useState(false)
  const client = useQueryClient()

  const attach = () => {
    if (!child) {
      toast('Choose a ticket first.', 'Nothing selected', 'error')
      return
    }
    setSaving(true)
    setTicketParent(id, Number(child))
      .then((reply) => {
        toast(reply.message, 'Attached', 'success')
        setChild('')
        void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id] })
      })
      /* `ApiError.message` e chiar `message` din raspunsul de 409, adica
         motivul lui `Tickets::setParent()`. */
      .catch((err: unknown) => toast(err instanceof Error ? err.message : 'Not attached', 'Error', 'error'))
      .finally(() => setSaving(false))
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  return (
    <div id={`sbTab${id}`}>
      {data.children.length > 0 && (
        <div className="sb-current">
          <span className="sb-muted" style={{ fontSize: 12 }}>
            Already depending on this ticket:
          </span>
          <br />
          {data.children.map((child) => (
            <span className="sb-chip" key={child.id}>
              <span className="sb-dot" style={{ background: child.color }}></span>#{child.id} {child.subject}
            </span>
          ))}
        </div>
      )}

      {data.parent ? (
        /*
         * Tichetul atarna deja de altul, deci nu poate fi el parinte. Fara
         * lista deloc, nu cu una care refuza la apasare: un camp pe care se
         * poate apasa promite ca se poate face ceva cu el.
         */
        <div className="sb-parent">
          <i className="fas fa-level-up-alt sb-muted"></i> This ticket already depends on{' '}
          <strong>
            #{data.parent.id} {data.parent.subject}
          </strong>
          .
          <span className="sb-help">
            A ticket that hangs under another one cannot take subordinates of its own &mdash; the family stays one level deep, so &ldquo;why is this waiting&rdquo; has one answer, not a chain of
            them.
          </span>
        </div>
      ) : data.candidates.length === 0 ? (
        <div className="sb-none">
          <i className="fas fa-info-circle"></i> No ticket of this customer can be attached here.
          {/*
           * Trei motive diferite arata identic - toate au deja un parinte, toate
           * sunt inchise, sau chiar nu mai e niciunul. Spuse, fiindca altfel omul
           * cauta o greseala acolo unde nu e una.
           */}
          <span className="sb-help">Only active or approved tickets that do not already hang under another one can be attached.</span>
        </div>
      ) : (
        <>
          <div className="sb-row">
            <div>
              <label htmlFor={`sbTicket${id}`}>Ticket to attach</label>
              {/*
                `data-subtext` din macheta (stadiul si data) sta in eticheta,
                dupa `&middot;`: cautarea lui bootstrap-select trece si prin el,
                deci se gaseste si dupa stadiu, nu numai dupa numar si subiect.
              */}
              <SelectPicker
                id={`sbTicket${id}`}
                className="form-control"
                title="Choose a ticket…"
                size={10}
                options={data.candidates.map((candidate) => ({
                  value: String(candidate.id),
                  label: `#${candidate.id} ${candidate.subject} · ${candidate.note}`,
                }))}
                value={child}
                onChange={setChild}
              />
            </div>

            <div>
              <button type="button" className="btn btn-primary sb-attach" disabled={saving} onClick={attach}>
                Set as Parent
              </button>
            </div>
          </div>

          <span className="sb-help">
            {data.candidates.length} ticket{data.candidates.length === 1 ? '' : 's'} can be attached.
          </span>
        </>
      )}

      <CausedByChange id={id} />
    </div>
  )
}

/**
 * Legatura "cauzat de change" - al doilea fel de rudenie din familie.
 *
 * Nu e parinte/copil: incidentul nu "atarna" de change si nu-l asteapta, doar
 * spune de unde a venit. De-aia nu trece prin lista de candidati de mai sus
 * (care ascunde tichetele cu parinte si pe cele inchise - iar un change care a
 * stricat ceva e de obicei deja inchis): se scrie numarul change-ului.
 * Serverul refuza un tichet care nu e change, unul al altui client, si un
 * change legat de alt change.
 */
function CausedByChange({ id }: { id: number }) {
  const card = useQuery(ticketQuery(id))
  const client = useQueryClient()
  const [change, setChange] = useState('')
  const [saving, setSaving] = useState(false)
  const linked = card.data?.family?.causedBy ?? []

  const done = (message: string) => {
    toast(message, 'Caused by change', 'success')
    setChange('')
    void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id] })
  }
  const fail = (err: unknown) => toast(err instanceof Error ? err.message : 'Not linked', 'Error', 'error')

  const link = () => {
    const number = Number(change.replace('#', '').trim())
    if (!Number.isInteger(number) || number < 1) {
      toast('Write the number of the change.', 'Nothing selected', 'error')
      return
    }
    setSaving(true)
    linkCausedBy(id, number)
      .then((reply) => done(reply.message))
      .catch(fail)
      .finally(() => setSaving(false))
  }

  return (
    <div className="sb-caused">
      <label htmlFor={`sbCause${id}`}>Caused by a change</label>
      {linked.length > 0 && (
        <div className="sb-current">
          {linked.map((c) => (
            <span className="sb-chip" key={c.id}>
              <span className="sb-dot" style={{ background: c.color }}></span>#{c.id} {c.subject}{' '}
              <button
                type="button"
                className="btn btn-link btn-xs sb-unlink"
                title="Remove this link"
                aria-label={`Remove the link to change ${c.id}`}
                onClick={() => {
                  unlinkCausedBy(id, c.id)
                    .then((reply) => done(reply.message))
                    .catch(fail)
                }}
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="sb-row">
        <div>
          <input
            id={`sbCause${id}`}
            type="text"
            inputMode="numeric"
            className="form-control"
            placeholder="Change ticket number, e.g. 4711"
            value={change}
            onChange={(e) => setChange(e.target.value)}
          />
        </div>
        <div>
          <button type="button" className="btn btn-default sb-cause" disabled={saving} onClick={link}>
            Mark as caused by it
          </button>
        </div>
      </div>
      <span className="sb-help">The incident shows the change that caused it, and the change lists the incidents it caused.</span>
    </div>
  )
}
